import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator, Validators
} from '@angular/forms';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PhoneInputComponent
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    }
  ]
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor, Validator {

  @Input() placeholder: string = 'Phone number';

  @Output() countryChange: EventEmitter<any> = new EventEmitter();
  @Output() errors: EventEmitter<any> = new EventEmitter();

  private validationError = {
    '-99': 'Phone number is invalid',
    0: 'Phone number is invalid',
    1: 'Invalid country code',
    2: 'Too short',
    3: 'Too long',
    4: 'Is possible local only',
    5: 'Invalid length'
  };

  public telOptions = {
    separateDialCode: true,
    placeholder: 'Phone Number *',
    initialCountry: 'gb',
  };

  public telInput = null;

  public phoneControl: FormControl = new FormControl('', [
    Validators.required
  ]);

  public touched = false;
  public disabled = false;

  private isUpdatingPhone = false;

  onChange = (value) => {};
  onTouched = () => {};
  onValidationChange: any = () => {};

  constructor() { }

  public onCountryChange(e): void {
    if (!e || !e.name || !e.dialCode) {
      return;
    }

    const countryName = e.name.split(' (')[0] || '';

    this.countryChange.emit(countryName);

    if (!this.isUpdatingPhone) {
      this.onChange(this.getInternationalNumber(this.phoneControl.value, e.dialCode));
      this.onValidationChange();
    }
  }

  ngOnInit() {
    this.phoneControl.valueChanges.subscribe(value => {
      if (this.telInput) {
        this.updatePhone(value);
      }
    });
  }

  private updatePhone(value: any): void {
    const compactValue = this.removeSpaces(value);

    this.isUpdatingPhone = true;

    try {
      if (compactValue.charAt(0) === '+') {
        this.telInput.setNumber(compactValue);
      }

      const country = this.telInput.getSelectedCountryData();

      if (!country || !country.dialCode) {
        this.phoneControl.setValue(compactValue, { emitEvent: false });
        this.onChange(compactValue);
        this.onValidationChange();
        return;
      }

      const nationalNumber = this.getNationalNumber(compactValue, country.dialCode);

      if (compactValue.charAt(0) === '+' || nationalNumber !== value) {
        this.phoneControl.setValue(nationalNumber, { emitEvent: false });
      }

      this.onChange(this.getInternationalNumber(nationalNumber, country.dialCode));
      this.onCountryChange(country);
      this.onValidationChange();
    } finally {
      this.isUpdatingPhone = false;
    }
  }

  private getNationalNumber(value: string, dialCode: string): string {
    const prefix = '+' + dialCode;

    return value.indexOf(prefix) === 0 && value.length > prefix.length
      ? value.slice(prefix.length)
      : value;
  }

  private getInternationalNumber(value: any, dialCode: string): string {
    const compactValue = this.removeSpaces(value);

    if (!compactValue || compactValue.charAt(0) === '+') {
      return compactValue;
    }

    return '+' + dialCode + compactValue;
  }

  private removeSpaces(value: any): string {
    return value == null ? '' : value.toString().replace(/\s/g, '');
  }

  public hasError(status): void {
    if (!status) {
      const errorCode = this.telInput.getValidationError();
      const phone = this.phoneControl.value;

      if (phone !== '') {
        this.phoneControl.setErrors({ 'name': this.validationError[errorCode] });

        this.errors.emit(this.phoneControl.errors);
      }
    }
  }

  public telInputObject(obg): void {
    this.telInput = obg;

    if (this.phoneControl.value) {
      this.updatePhone(this.phoneControl.value);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.phoneControl.errors) {
      return this.phoneControl.errors;
    } else {
      return null;
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  writeValue(control: any): void {
    this.phoneControl.setValue(control);
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

}
